import React from 'react';
import { Phone, Calendar, MessageSquare, UserCheck, Globe, Star, ShieldCheck, ArrowRight, Zap, Check } from 'lucide-react';
import { motion } from 'framer-motion';
const websiteTemplates = [{
  image: 'https://cdn.magicpatterns.com/uploads/7qRfREV5VdxYj4ArEZPrT8/image.png',
  industry: 'Landscaping Company',
  description: 'Beautiful outdoor spaces deserve a beautiful online presence.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/wT4Cqb29coQu16tN6gvFVY/image.png',
  industry: 'General Contractor',
  description: 'Build trust with clients before the first handshake.'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/sxZ75FqbthVnQJmiAwoJ2Y/image.png',
  industry: 'HVAC Services',
  description: 'Keep your business running cool with a hot website.'
}];
const featurePills = ['Mobile Responsive', 'SEO Optimized', 'Fast Loading', 'Custom Design'];
const reviewPills = ['Fully Automated', 'Protects Your Reputation', 'Boosts Google Ranking', 'More 5-Star Reviews'];
export function SolutionSection() {
  return <section id="solutions" className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-389">
      <div className="absolute inset-0 gradient-animated-subtle pointer-events-none" data-id="element-390" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-391">
        {/* ========== 1. WEBSITE IN A DAY ========== */}
        <div className="mb-12 md:mb-16" data-id="element-392">
          <div className="text-center mb-8 md:mb-12" data-id="element-393">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-black text-black mb-3 md:mb-4" data-id="element-394">
              WEBSITE IN A DAY
            </motion.h2>
            <div className="w-20 md:w-24 h-1 bg-primary mx-auto mb-3 md:mb-4" data-id="element-395" />
            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="text-lg md:text-xl text-gray-600 font-medium" data-id="element-396">
              Custom, Clean Websites Built For Your Business - Fast
            </motion.p>
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="text-center text-gray-500 max-w-3xl mx-auto mb-8 md:mb-12 leading-relaxed text-sm md:text-base" data-id="element-397">
            No more waiting weeks for your website. We design and launch
            professional, mobile-responsive websites in as little as 24 hours.
            Each site is tailored to your industry, optimized for search
            engines, and built to convert visitors into customers.
          </motion.p>

          {/* Template Grid */}
          <div className="grid md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-10" data-id="element-398">
            {websiteTemplates.map((template, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: i * 0.15,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-gray-100 group" data-id="element-399">
                <div className="aspect-[4/3] overflow-hidden" data-id="element-400">
                  <img src={template.image} alt={`${template.industry} website template`} className="w-full h-full object-cover object-top" data-id="element-401" />
                </div>
                <div className="p-4 md:p-5" data-id="element-402">
                  <h3 className="text-base md:text-lg font-bold text-black mb-1" data-id="element-403">
                    {template.industry}
                  </h3>
                  <p className="text-gray-500 text-xs md:text-sm" data-id="element-404">
                    {template.description}
                  </p>
                </div>
              </motion.div>)}
          </div>

          {/* Feature Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.5,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-405">
            {featurePills.map((pill, i) => <span key={i} className="bg-primary/10 text-primary text-xs md:text-sm font-semibold px-3 md:px-4 py-1.5 md:py-2 rounded-full" data-id="element-406">
                {pill}
              </span>)}
          </motion.div>
        </div>

        {/* ========== DIVIDER ========== */}
        <div className="border-t border-gray-200 my-8 md:my-10" data-id="element-407" />

        {/* ========== 2. REPUTATION MANAGEMENT / 5-STAR REVIEWS ========== */}
        <div className="mb-12 md:mb-16" data-id="element-408">
          <div className="text-center mb-8 md:mb-12" data-id="element-409">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-black text-black mb-3 md:mb-4" data-id="element-410">
              WE GET YOU 5-STAR GOOGLE REVIEWS, FAST
            </motion.h2>
            <div className="w-20 md:w-24 h-1 bg-primary mx-auto mb-3 md:mb-4" data-id="element-411" />
            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="text-lg md:text-xl text-gray-600 font-medium" data-id="element-412">
              Automated Reputation Management That Protects & Grows Your Brand
            </motion.p>
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="text-center text-gray-500 max-w-3xl mx-auto mb-8 md:mb-12 leading-relaxed text-sm md:text-base" data-id="element-413">
            We automatically reach out to your past customers and ask them to
            rate their experience. Happy customers (4-5 stars) are redirected
            straight to your Google Business page to leave a public review.
            Unhappy customers (1-3 stars) are sent to a private feedback form
            instead, so negative reviews never hit Google. You grow your rating
            while catching issues before they go public.
          </motion.p>

          {/* How It Works - Visual Flow */}
          <div className="grid lg:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12 items-stretch" data-id="element-414">
            {/* Left: Happy Path */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex flex-col" data-id="element-415">
              <div className="overflow-hidden flex-1" data-id="element-416">
                <img src="https://cdn.magicpatterns.com/uploads/ms7MNCb3gqbQHc6XKZ9hs8/image.png" alt="Customer rating form - How would you rate us?" className="w-full h-full object-cover object-top" data-id="element-417" />
              </div>
              <div className="p-4 md:p-6" data-id="element-418">
                <div className="flex items-center gap-2 mb-2 md:mb-3" data-id="element-419">
                  <div className="flex" data-id="element-420">
                    {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" data-id="element-421" />)}
                  </div>
                  <ArrowRight size={14} className="text-gray-400" data-id="element-422" />
                  <span className="text-xs md:text-sm font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded" data-id="element-423">
                    Google Review
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-bold text-black mb-1" data-id="element-424">
                  Happy Customer? → Google Review
                </h3>
                <p className="text-gray-500 text-xs md:text-sm" data-id="element-425">
                  When a customer selects 4 or 5 stars, they're automatically
                  redirected to your Google Business page to leave a public
                  review, boosting your rating instantly.
                </p>
              </div>
            </motion.div>

            {/* Right: Unhappy Path */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex flex-col" data-id="element-426">
              <div className="overflow-hidden flex-1" data-id="element-427">
                <img src="https://cdn.magicpatterns.com/uploads/wSXkWBTsVztun6Z63KuUDr/image.png" alt="Private feedback form for unsatisfied customers" className="w-full h-full object-cover object-top" data-id="element-428" />
              </div>
              <div className="p-4 md:p-6" data-id="element-429">
                <div className="flex items-center gap-2 mb-2 md:mb-3" data-id="element-430">
                  <div className="flex" data-id="element-431">
                    {[...Array(2)].map((_, i) => <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" data-id="element-432" />)}
                    {[...Array(3)].map((_, i) => <Star key={i} size={14} className="text-gray-300" data-id="element-433" />)}
                  </div>
                  <ArrowRight size={14} className="text-gray-400" data-id="element-434" />
                  <span className="text-xs md:text-sm font-bold text-primary bg-primary/10 px-2 py-0.5 rounded" data-id="element-435">
                    Private Feedback
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-bold text-black mb-1" data-id="element-436">
                  Unhappy? → Private Feedback Only
                </h3>
                <p className="text-gray-500 text-xs md:text-sm" data-id="element-437">
                  If a customer selects 1, 2, or 3 stars, they're shown a
                  private feedback form instead. Their concerns come directly to
                  you, never to Google.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Review Feature Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.4,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-438">
            {reviewPills.map((pill, i) => <span key={i} className="bg-primary/10 text-primary text-xs md:text-sm font-semibold px-3 md:px-4 py-1.5 md:py-2 rounded-full" data-id="element-439">
                {pill}
              </span>)}
          </motion.div>
        </div>

        {/* ========== DIVIDER ========== */}
        <div className="border-t border-gray-200 my-8 md:my-10" data-id="element-440" />

        {/* ========== 3. WE AUTOMATE IT ALL ========== */}
        <div data-id="element-441">
          <div className="text-center mb-8 md:mb-12" data-id="element-442">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-black text-black mb-3 md:mb-4" data-id="element-443">
              WE AUTOMATE IT ALL
            </motion.h2>
            <div className="w-20 md:w-24 h-1 bg-primary mx-auto mb-3 md:mb-4" data-id="element-444" />
            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: 'easeOut'
          }} viewport={{
            once: true
          }} className="text-lg md:text-xl text-gray-600 font-medium" data-id="element-445">
              Book More Appointments. Make More Money. Save Hours Every Week.
            </motion.p>
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="text-center text-gray-500 max-w-3xl mx-auto mb-8 md:mb-12 leading-relaxed text-sm md:text-base" data-id="element-446">
            Your competitors are losing leads every single day because nobody
            answers at 2 AM or follows up fast enough. Our AI works around the
            clock on your website and phone lines, turning missed opportunities
            into booked appointments and real revenue while you sleep.
          </motion.p>

          {/* AI Receptionist Showcase - Full Width */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="grid lg:grid-cols-2 gap-6 md:gap-8 items-stretch mb-8 md:mb-10" data-id="element-447">
            {/* Left Side - Expanded Phone Mockup */}
            <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-gray-100" data-id="element-448">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 pb-0 h-full" data-id="element-449">
                <div className="bg-white rounded-t-xl md:rounded-t-2xl shadow-md border border-gray-200 overflow-hidden h-full flex flex-col" data-id="element-450">
                  {/* Phone Header */}
                  <div className="bg-gray-900 px-3 md:px-4 py-2 md:py-3 flex items-center justify-between" data-id="element-451">
                    <span className="text-white text-[10px] md:text-xs font-medium" data-id="element-452">
                      9:41 AM
                    </span>
                    <div className="flex items-center gap-1" data-id="element-453">
                      <div className="w-3 h-1.5 md:w-3.5 md:h-2 border border-white/60 rounded-sm relative" data-id="element-454">
                        <div className="absolute inset-0.5 bg-green-400 rounded-[1px]" data-id="element-455" />
                      </div>
                    </div>
                  </div>

                  {/* Call Interface */}
                  <div className="p-3 md:p-4 space-y-2 md:space-y-3 flex-1" data-id="element-456">
                    {/* Incoming Call Display */}
                    <div className="text-center mb-2" data-id="element-457">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-1.5" data-id="element-458">
                        <Phone size={18} className="text-primary" data-id="element-459" />
                      </div>
                      <p className="text-[10px] md:text-xs text-gray-400 font-medium" data-id="element-460">
                        Incoming Call
                      </p>
                      <p className="text-sm md:text-base font-bold text-black" data-id="element-461">
                        +44 7700 900123
                      </p>
                    </div>

                    {/* Step 1: AI Answered */}
                    <div className="bg-green-50 border border-green-200 rounded-lg md:rounded-xl p-2.5 md:p-3" data-id="element-462">
                      <div className="flex items-center gap-2 mb-1" data-id="element-463">
                        <span className="relative flex h-1.5 w-1.5 md:h-2 md:w-2" data-id="element-464">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-465"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 md:h-2 md:w-2 bg-green-400" data-id="element-466"></span>
                        </span>
                        <span className="text-green-700 text-[10px] md:text-xs font-bold" data-id="element-467">
                          AI Receptionist Answered
                        </span>
                      </div>
                      <p className="text-green-800 text-[11px] md:text-sm leading-snug" data-id="element-468">
                        "Thanks for calling! Let me check our availability and
                        get you scheduled right away."
                      </p>
                    </div>

                    {/* Step 2: Customer Need Identified */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg md:rounded-xl p-2.5 md:p-3" data-id="element-469">
                      <div className="flex items-center gap-2 mb-1" data-id="element-470">
                        <Zap size={10} className="text-amber-600" data-id="element-471" />
                        <span className="text-amber-700 text-[10px] md:text-xs font-bold" data-id="element-472">
                          Customer Need Identified
                        </span>
                      </div>
                      <p className="text-amber-800 text-[11px] md:text-sm leading-snug" data-id="element-473">
                        Emergency plumbing repair, dispatching nearest available
                        tech
                      </p>
                    </div>

                    {/* Step 3: Appointment Booked */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg md:rounded-xl p-2.5 md:p-3" data-id="element-474">
                      <div className="flex items-center gap-2 mb-1" data-id="element-475">
                        <Calendar size={10} className="text-blue-600" data-id="element-476" />
                        <span className="text-blue-700 text-[10px] md:text-xs font-bold" data-id="element-477">
                          Appointment Booked
                        </span>
                      </div>
                      <p className="text-blue-800 text-[11px] md:text-sm leading-snug" data-id="element-478">
                        Tomorrow, 9:00 AM. Confirmation SMS sent to customer
                      </p>
                    </div>

                    {/* Step 4: Automated Follow-Up */}
                    <div className="bg-violet-50 border border-violet-200 rounded-lg md:rounded-xl p-2.5 md:p-3" data-id="element-479">
                      <div className="flex items-center gap-2 mb-1" data-id="element-480">
                        <MessageSquare size={10} className="text-violet-600" data-id="element-481" />
                        <span className="text-violet-700 text-[10px] md:text-xs font-bold" data-id="element-482">
                          Automated Follow-Up
                        </span>
                      </div>
                      <p className="text-violet-800 text-[11px] md:text-sm leading-snug" data-id="element-483">
                        Reminder SMS sent 24hrs before appointment + directions
                      </p>
                    </div>

                    {/* Step 5: Lead Saved to CRM */}
                    <div className="bg-primary/5 border border-primary/20 rounded-lg md:rounded-xl p-2.5 md:p-3" data-id="element-484">
                      <div className="flex items-center gap-2 mb-1" data-id="element-485">
                        <UserCheck size={10} className="text-primary" data-id="element-486" />
                        <span className="text-primary text-[10px] md:text-xs font-bold" data-id="element-487">
                          Lead Saved to CRM
                        </span>
                      </div>
                      <p className="text-gray-600 text-[11px] md:text-sm leading-snug" data-id="element-488">
                        Full contact info, call recording, and notes saved
                        automatically
                      </p>
                    </div>

                    {/* Step 6: Review Request Sent */}
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg md:rounded-xl p-2.5 md:p-3" data-id="element-489">
                      <div className="flex items-center gap-2 mb-1" data-id="element-490">
                        <Star size={10} className="text-yellow-600" data-id="element-491" />
                        <span className="text-yellow-700 text-[10px] md:text-xs font-bold" data-id="element-492">
                          Review Request Sent
                        </span>
                      </div>
                      <p className="text-yellow-800 text-[11px] md:text-sm leading-snug" data-id="element-493">
                        5-star review request sent automatically after job
                        completion
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Outcome Details Panel */}
            <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-5 md:p-8 flex flex-col justify-between" data-id="element-494">
              {/* Header */}
              <div data-id="element-495">
                <h3 className="text-xl md:text-2xl font-black text-black mb-1" data-id="element-496">
                  Every Call Becomes Revenue
                </h3>
                <p className="text-gray-500 text-sm md:text-base mb-6" data-id="element-497">
                  What happens when you never miss a call
                </p>

                {/* Outcome Stats Grid */}
                <div className="grid grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-498">
                  <div className="text-center md:text-left" data-id="element-499">
                    <div className="text-2xl md:text-3xl font-black text-black" data-id="element-500">
                      0
                    </div>
                    <div className="text-xs md:text-sm text-gray-500" data-id="element-501">
                      Missed Calls
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-400 mt-0.5" data-id="element-502">
                      Every call answered, day or night
                    </div>
                  </div>
                  <div className="text-center md:text-left" data-id="element-503">
                    <div className="text-2xl md:text-3xl font-black text-black" data-id="element-504">
                      3x
                    </div>
                    <div className="text-xs md:text-sm text-gray-500" data-id="element-505">
                      More Bookings
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-400 mt-0.5" data-id="element-506">
                      Automated scheduling fills your calendar
                    </div>
                  </div>
                  <div className="text-center md:text-left" data-id="element-507">
                    <div className="text-2xl md:text-3xl font-black text-black" data-id="element-508">
                      85%
                    </div>
                    <div className="text-xs md:text-sm text-gray-500" data-id="element-509">
                      Less Admin
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-400 mt-0.5" data-id="element-510">
                      No manual follow-ups or data entry
                    </div>
                  </div>
                  <div className="text-center md:text-left" data-id="element-511">
                    <div className="text-2xl md:text-3xl font-black text-primary" data-id="element-512">
                      £4,200+
                    </div>
                    <div className="text-xs md:text-sm text-gray-500" data-id="element-513">
                      Monthly
                    </div>
                    <div className="text-[10px] md:text-xs text-gray-400 mt-0.5" data-id="element-514">
                      Average revenue recovered
                    </div>
                  </div>
                </div>

                {/* Benefit List */}
                <div className="space-y-3 mb-6 md:mb-8" data-id="element-515">
                  <div className="flex items-start gap-3" data-id="element-516">
                    <div className="mt-0.5 bg-green-500 rounded-full p-0.5 flex-shrink-0" data-id="element-517">
                      <Check size={12} className="text-white" data-id="element-518" />
                    </div>
                    <span className="text-gray-700 text-sm md:text-base" data-id="element-519">
                      Answers calls in your brand voice so customers never know
                      it's AI
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-520">
                    <div className="mt-0.5 bg-green-500 rounded-full p-0.5 flex-shrink-0" data-id="element-521">
                      <Check size={12} className="text-white" data-id="element-522" />
                    </div>
                    <span className="text-gray-700 text-sm md:text-base" data-id="element-523">
                      Books appointments directly into your calendar
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-524">
                    <div className="mt-0.5 bg-green-500 rounded-full p-0.5 flex-shrink-0" data-id="element-525">
                      <Check size={12} className="text-white" data-id="element-526" />
                    </div>
                    <span className="text-gray-700 text-sm md:text-base" data-id="element-527">
                      Sends confirmation and reminder texts automatically
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-528">
                    <div className="mt-0.5 bg-green-500 rounded-full p-0.5 flex-shrink-0" data-id="element-529">
                      <Check size={12} className="text-white" data-id="element-530" />
                    </div>
                    <span className="text-gray-700 text-sm md:text-base" data-id="element-531">
                      Captures every lead with full details in your CRM
                    </span>
                  </div>
                </div>
              </div>

              {/* Testimonial Quote */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-532">
                <p className="text-gray-700 text-sm md:text-base italic mb-2" data-id="element-533">
                  "We went from missing 40% of calls to booking every single
                  one. It paid for itself in the first week."
                </p>
                <p className="text-gray-500 text-xs md:text-sm font-medium" data-id="element-534">
                  - Mike R., HVAC Contractor
                </p>
              </div>
            </div>
          </motion.div>

          {/* Description Section - Full Width Centered */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="text-center max-w-3xl mx-auto mb-10 md:mb-14" data-id="element-535">
            <div className="flex items-center justify-center gap-2 mb-2 md:mb-3" data-id="element-536">
              <span className="relative flex h-2 w-2 md:h-2.5 md:w-2.5" data-id="element-537">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-538"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 md:h-2.5 md:w-2.5 bg-green-400" data-id="element-539"></span>
              </span>
              <span className="text-xs md:text-sm font-bold text-green-600" data-id="element-540">
                Never Misses a Call
              </span>
            </div>
            <h3 className="text-lg md:text-xl font-black text-black mb-1.5 md:mb-2" data-id="element-541">
              AI Receptionist That Answers Calls and Books Jobs on Autopilot
            </h3>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed" data-id="element-542">
              Every missed call is money left on the table. Our AI receptionist
              picks up every single call, answers customer questions, and books
              appointments directly into your calendar, 24 hours a day, 7 days a
              week, even on holidays.
            </p>
          </motion.div>

          {/* Outcome Stats Banner */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.3,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="gradient-animated rounded-xl md:rounded-2xl p-6 md:p-8 mb-8 md:mb-10" data-id="element-543">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center" data-id="element-544">
              <div data-id="element-545">
                <div className="text-white text-2xl md:text-4xl font-black" data-id="element-546">
                  24/7
                </div>
                <div className="text-white/70 text-xs md:text-sm font-medium mt-1" data-id="element-547">
                  Always On, Never Off
                </div>
              </div>
              <div data-id="element-548">
                <div className="text-white text-2xl md:text-4xl font-black" data-id="element-549">
                  3x
                </div>
                <div className="text-white/70 text-xs md:text-sm font-medium mt-1" data-id="element-550">
                  More Appointments Booked
                </div>
              </div>
              <div data-id="element-551">
                <div className="text-white text-2xl md:text-4xl font-black" data-id="element-552">
                  0
                </div>
                <div className="text-white/70 text-xs md:text-sm font-medium mt-1" data-id="element-553">
                  Missed Calls or Leads
                </div>
              </div>
              <div data-id="element-554">
                <div className="text-white text-2xl md:text-4xl font-black" data-id="element-555">
                  10+
                </div>
                <div className="text-white/70 text-xs md:text-sm font-medium mt-1" data-id="element-556">
                  Hours Saved Per Week
                </div>
              </div>
            </div>
          </motion.div>

          {/* Automation Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.4,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="flex flex-wrap justify-center gap-2 md:gap-3" data-id="element-557">
            {['24/7 AI ChatBot', 'AI Receptionist', 'Auto-Books Appointments', 'Saves to CRM', 'Zero Missed Leads'].map((pill, i) => <span key={i} className="bg-primary/10 text-primary text-xs md:text-sm font-semibold px-3 md:px-4 py-1.5 md:py-2 rounded-full" data-id="element-558">
                {pill}
              </span>)}
          </motion.div>
        </div>
      </div>
    </section>;
}